import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase/config';
import axios from 'axios';
import { setToken, getToken, removeToken } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasFetched, setHasFetched] = useState(false);

  // Define clearUserData function locally to avoid circular dependency
  const clearUserData = () => {
    console.log("Auth context clearing user data");
  };

  // Configure axios defaults for authentication
  const configureAxios = async (user) => {
    try {
      if (user) {
        // Force refresh the token
        const token = await user.getIdToken(true);
        console.log("Fresh token from Firebase:", token.substring(0, 20) + "...");
        
        // Store and configure axios
        setToken(token); // This now updates both localStorage and axios headers
        console.log("Axios configured with fresh token");
      } else {
        // Clear token
        removeToken(); // Remove token from localStorage
        delete axios.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      console.error("Error configuring Axios:", error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log("Token restored from localStorage on app init");
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", user ? "User logged in" : "No user");
      
      setCurrentUser(user);
      
      if (user) {
        // Get fresh token
        try {
          const token = await user.getIdToken(true);
          setToken(token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log("Token refreshed on auth state change");
        } catch (error) {
          console.error("Error getting token:", error);
        }
      } else {
        removeToken();
        delete axios.defaults.headers.common['Authorization'];
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  // Register with email and password
  const signup = async (email, password) => {
    try {
      setError('');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await configureAxios(user);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // IMPORTANT: Get fresh token and store it BEFORE setting currentUser
      const token = await user.getIdToken(true);
      setToken(token); // This now updates both localStorage and axios headers
      console.log("Login successful, token set");
      
      // Only now set the current user
      setCurrentUser(user);
      
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      await configureAxios(user);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      clearUserData();
      setHasFetched(false);
      await configureAxios(null); // Clear Axios config and token
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    error,
    setError,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;